import { config } from "@/config/environment";

/**
 * Clears all PWA caches to resolve conflicting entries
 */
export async function clearPWACaches(): Promise<void> {
  if (!("caches" in window)) {
    console.warn("Cache API not supported");
    return;
  }

  try {
    const cacheNames = await caches.keys();

    await Promise.all(
      cacheNames.map(async (cacheName) => {
        const deleted = await caches.delete(cacheName);
        if (config.debug.enabled) {
          console.log(`Cache ${cacheName} deleted:`, deleted);
        }
        return deleted;
      })
    );

  } catch (error) {
    console.error("Failed to clear PWA caches:", error);
    throw error;
  }
}

/**
 * Clears specific cache by name
 */
export async function clearCacheByName(cacheName: string): Promise<boolean> {
  if (!("caches" in window)) {
    console.warn("Cache API not supported");
    return false;
  }

  try {
    const deleted = await caches.delete(cacheName);
    if (config.debug.enabled) {
      console.log(`Cache ${cacheName} deleted:`, deleted);
    }
    return deleted;
  } catch (error) {
    console.error(`Failed to clear cache ${cacheName}:`, error);
    return false;
  }
}

/**
 * Lists all available caches
 */
export async function listCaches(): Promise<string[]> {
  if (!("caches" in window)) {
    console.warn("Cache API not supported");
    return [];
  }

  try {
    const cacheNames = await caches.keys();
    if (config.debug.enabled) {
      console.log("Available caches:", cacheNames);
    }
    return cacheNames;
  } catch (error) {
    console.error("Failed to list caches:", error);
    return [];
  }
}

/**
 * Unregisters all service workers
 */
export async function unregisterServiceWorkers(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker API not supported");
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    await Promise.all(
      registrations.map(async (registration) => {
        const unregistered = await registration.unregister();
        if (config.debug.enabled) {
          console.log("Service worker unregistered:", unregistered);
        }
        return unregistered;
      })
    );

  } catch (error) {
    console.error("Failed to unregister service workers:", error);
    throw error;
  }
}

/**
 * Forces a service worker update
 */
export async function forceServiceWorkerUpdate(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker API not supported");
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
    }
  } catch (error) {
    console.error("Failed to force service worker update:", error);
    throw error;
  }
}

/**
 * Caches that hold precached build assets, and so are the ones a revision
 * conflict can occur in. Shared by the check and the fix: if the two disagreed,
 * the UI could report a conflict in a cache the fix would not clear, leaving a
 * banner that never goes away.
 *
 * Runtime caches for API and identity responses are deliberately absent — they
 * are not precached, are not revisioned, and are handled by
 * `cleanupRetiredRuntimeCaches`.
 */
function selectPrecacheCacheNames(cacheNames: string[]): string[] {
  return cacheNames.filter(
    (name) =>
      name.includes("workbox") ||
      name.includes("pwa") ||
      name.includes("static") ||
      name.includes("images")
  );
}

/**
 * Resolves PWA cache conflicts by clearing conflicting entries
 * This function helps fix the "add-to-cache-list-conflicting-entries" error
 */
export async function resolvePWACacheConflicts(): Promise<void> {
  if (!("caches" in window)) {
    console.warn("Cache API not supported");
    return;
  }

  try {
    const cacheNames = await caches.keys();
    const workboxCaches = selectPrecacheCacheNames(cacheNames);

    // Clear all workbox-related caches
    await Promise.all(
      workboxCaches.map((cacheName) => caches.delete(cacheName))
    );

    // Reload the page to rebuild caches
    window.location.reload();
  } catch (error) {
    console.error("Failed to resolve PWA cache conflicts:", error);
  }
}

/**
 * Workbox versions a precached entry by appending `?__WB_REVISION__=<hash>` to
 * its URL, so the same file legitimately appears under several URLs over the
 * life of a cache. Comparing raw URLs would report every precached asset as a
 * duplicate, which is why the revision is stripped before grouping.
 */
const WORKBOX_REVISION_PARAM = "__WB_REVISION__";

/**
 * Reduces a cache entry URL to the identity of the file it holds.
 *
 * Only Workbox's revision marker is dropped. Any other query string is part of
 * the resource identity — `/api/list?page=1` and `/api/list?page=2` are
 * different resources, and collapsing them would invent conflicts.
 */
function stripWorkboxRevision(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete(WORKBOX_REVISION_PARAM);
    // Keep `?a=1` distinct from `?a=1&__WB_REVISION__=x`, but avoid leaving a
    // bare `?` behind once the only parameter has been removed.
    const query = parsed.searchParams.toString();
    return `${parsed.origin}${parsed.pathname}${query ? `?${query}` : ""}`;
  } catch {
    // A cache key that will not parse as a URL is compared verbatim rather
    // than being silently grouped with everything else that fails to parse.
    return url;
  }
}

/**
 * Checks for PWA cache conflicts and returns details.
 *
 * A conflict means one file is held at two different Workbox revisions in the
 * same cache, which can serve a stale asset alongside a current one. Two URLs
 * that differ only by revision across a deploy are normal and are not reported.
 */
export async function checkPWACacheConflicts(): Promise<{
  hasConflicts: boolean;
  cacheNames: string[];
  details: string[];
}> {
  if (!("caches" in window)) {
    return {
      hasConflicts: false,
      cacheNames: [],
      details: ["Cache API not supported"]
    };
  }

  try {
    const cacheNames = await caches.keys();
    const workboxCaches = selectPrecacheCacheNames(cacheNames);

    const details: string[] = [];
    let hasConflicts = false;

    // Check for potential conflicts
    for (const cacheName of workboxCaches) {
      try {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();

        // Group by the file's identity, ignoring Workbox's revision marker, so
        // that one entry per file reads as healthy however it is versioned.
        const urlMap = new Map<string, string[]>();
        for (const request of requests) {
          const identity = stripWorkboxRevision(request.url);
          if (!urlMap.has(identity)) {
            urlMap.set(identity, []);
          }
          urlMap.get(identity)!.push(request.url);
        }

        // Two entries for one file are only a conflict when their revisions
        // actually differ. Cache keys are unique per URL, so identical URLs
        // cannot collide here; differing revisions can, and that is the case
        // where a stale asset may be served alongside the current one.
        for (const [identity, urls] of urlMap.entries()) {
          const distinctRevisions = new Set(urls);
          if (distinctRevisions.size > 1) {
            hasConflicts = true;
            details.push(
              `Conflict in ${cacheName}: ${identity} has ` +
                `${distinctRevisions.size} versions`
            );
          }
        }
      } catch (error) {
        details.push(`Error checking cache ${cacheName}: ${error}`);
      }
    }

    return {
      hasConflicts,
      cacheNames: workboxCaches,
      details
    };
  } catch (error) {
    return {
      hasConflicts: false,
      cacheNames: [],
      details: [`Error checking caches: ${error}`]
    };
  }
}

// PWA Install Prompt Utilities

/**
 * Check if the app is running as a PWA
 */
export function isPWA(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
}

/**
 * Check if the app can be installed
 */
export function canInstallPWA(): boolean {
  // More comprehensive check for PWA installability
  return (
    "serviceWorker" in navigator && "PushManager" in window && !isPWA() // Don't show install prompt if already installed
  );
}

/**
 * Get PWA installation prompt
 * Note: This function should only be used if no other beforeinstallprompt listener exists
 */
export function getInstallPrompt(): Promise<BeforeInstallPromptEvent | null> {
  return new Promise((resolve) => {
    // Check if the event has already been fired and stored
    const globalWindow = window as Window & {
      __deferredPrompt?: BeforeInstallPromptEvent;
    };
    if (globalWindow.__deferredPrompt) {
      const prompt = globalWindow.__deferredPrompt;
      delete globalWindow.__deferredPrompt;
      resolve(prompt);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      resolve(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler, { once: true });

    // Resolve null if no prompt after a short delay
    setTimeout(() => resolve(null), 1000);
  });
}

/**
 * Install PWA using a provided prompt event
 */
export async function installPWAWithPrompt(
  prompt: BeforeInstallPromptEvent
): Promise<boolean> {
  try {
    // Check if the prompt is still valid
    if (!prompt || typeof prompt.prompt !== "function") {
      if (config.debug.enabled) {
        console.warn("PWA prompt is not valid or has already been used");
      }
      return false;
    }

    // Show the install prompt
    await prompt.prompt();

    // Wait for user choice
    const result = await prompt.userChoice;

    if (config.debug.enabled) {
      console.log("PWA install user choice:", result.outcome);
    }

    return result.outcome === "accepted";
  } catch (error) {
    if (config.debug.enabled) {
      console.error("PWA installation failed:", error);
    }
    return false;
  }
}

/**
 * Install PWA (fallback method)
 */
export async function installPWA(): Promise<boolean> {
  try {
    const prompt = await getInstallPrompt();
    if (prompt) {
      return await installPWAWithPrompt(prompt);
    }
    return false;
  } catch (error) {
    if (config.debug.enabled) {
      console.error("PWA installation failed:", error);
    }
    return false;
  }
}

/**
 * Check if this is the first time the user is visiting the PWA
 */
export function isFirstTimeVisit(): boolean {
  const hasVisitedBefore = localStorage.getItem("pwa-has-visited");
  return !hasVisitedBefore;
}

/**
 * Mark that the user has visited the PWA
 */
export function markAsVisited(): void {
  localStorage.setItem("pwa-has-visited", "true");
}

/**
 * Runtime caches that were removed because they could retain a failed or
 * opaque response and replay it indefinitely. Any cache listed here is deleted
 * once, on the first load after this ships.
 */
const RETIRED_RUNTIME_CACHES = ["api-cache", "msal-cache"] as const;

/**
 * Bump this when another retired cache is added above, so the cleanup runs
 * again for clients that already completed an earlier revision.
 */
const RUNTIME_CACHE_CLEANUP_REVISION = "1";

const RUNTIME_CACHE_CLEANUP_KEY = "pwa-runtime-cache-cleanup";

/**
 * Deletes retired runtime caches left behind by an earlier service worker.
 *
 * `api-cache` was a NetworkFirst cache that accepted opaque (`0`) responses, so
 * a single failed API request could be stored and then served for every later
 * request to the same URL — appearing as a `404 (from service worker)` that
 * persisted long after the API recovered. Changing the caching strategy stops
 * new entries being written, but does nothing about entries already on disk:
 * Workbox's `cleanupOutdatedCaches` only prunes the precache. Existing clients
 * therefore stay broken until the stale cache is removed explicitly.
 *
 * Safe to call on every startup — it is a no-op once it has run.
 */
export async function cleanupRetiredRuntimeCaches(): Promise<void> {
  if (!("caches" in window)) {
    return;
  }

  try {
    if (
      localStorage.getItem(RUNTIME_CACHE_CLEANUP_KEY) ===
      RUNTIME_CACHE_CLEANUP_REVISION
    ) {
      return;
    }
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Fall through
    // and clean up anyway; the cost is repeating a few no-op deletes.
  }

  try {
    const cacheNames = await caches.keys();
    const staleCaches = cacheNames.filter((name) =>
      RETIRED_RUNTIME_CACHES.some(
        (retired) => name === retired || name.includes(retired)
      )
    );

    await Promise.all(staleCaches.map((name) => caches.delete(name)));

    if (config.debug.enabled && staleCaches.length > 0) {
      console.log("Removed retired runtime caches:", staleCaches);
    }

    try {
      localStorage.setItem(
        RUNTIME_CACHE_CLEANUP_KEY,
        RUNTIME_CACHE_CLEANUP_REVISION
      );
    } catch {
      // Non-fatal: without the marker the cleanup simply runs again next load.
    }
  } catch (error) {
    // Never block startup on cache maintenance.
    console.error("Failed to clean up retired runtime caches:", error);
  }
}

/**
 * Check if the app is offline
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}

/**
 * Register offline/online event listeners
 */
export function registerOnlineStatusListener(
  onOnline: () => void,
  onOffline: () => void
): () => void {
  const handleOnline = () => onOnline();
  const handleOffline = () => onOffline();

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}
